import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getDeviceModel,
  getDeviceModelsByIds,
  getDeviceModelsBySearch,
  getPaginatedDeviceModels,
} from "@/api/resources/deviceModels/api";
import i18n from "@/providers/i18n";

import type { GetPaginatedDeviceModelsProps, GetSearchDeviceModelsProps } from "@/api/resources/deviceModels/types";

const entity = i18n.t("device-model");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("device-models");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const deviceModelsQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "deviceModels"],
  lists: () => [...deviceModelsQueryKeys.all(), "list"],
  list: (workspace_id?: string) => [...deviceModelsQueryKeys.lists(), "all", workspace_id],
  listByIds: (ids?: string[]) => [...deviceModelsQueryKeys.lists(), "byIds", ids],
  listPaginated: (queryKeyPaginationFilters: GetPaginatedDeviceModelsProps) => [
    ...deviceModelsQueryKeys.lists(),
    "paginated",
    queryKeyPaginationFilters,
  ],
  listBySearch: (searchOptionFilters: GetSearchDeviceModelsProps) => [
    ...deviceModelsQueryKeys.lists(),
    "bySearch",
    searchOptionFilters,
  ],
  byId: (id?: string) => [...deviceModelsQueryKeys.all(), "byId", id],
};

export const deviceModelsQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...deviceModelsQueryKeys.listByIds(ids)],
      queryFn: () =>
        getDeviceModelsByIds({
          keyIn: "id",
          idsIn: ids,
        }),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listPaginated: (queryKeyPaginationFilters: GetPaginatedDeviceModelsProps) =>
    queryOptions({
      queryKey: [...deviceModelsQueryKeys.listPaginated(queryKeyPaginationFilters)],
      queryFn: () => getPaginatedDeviceModels(queryKeyPaginationFilters),

      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listBySearch: (searchOptionFilters: GetSearchDeviceModelsProps) =>
    queryOptions({
      queryKey: [...deviceModelsQueryKeys.listBySearch(searchOptionFilters)],
      queryFn: () => getDeviceModelsBySearch(searchOptionFilters),
      placeholderData: (prev) => prev, // ! this prevents flashing of results when using search in selectors
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...deviceModelsQueryKeys.byId(id)],
      queryFn: id ? () => getDeviceModel({ match: { id } }) : skipToken,

      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
